import React, { lazy } from 'react';
import { motion } from 'framer-motion';
import { CheckIcon, CodeIcon, CornerDownRightIcon } from 'lucide-react';
import { Container } from '../components/Container';
const requestLines: {
  tone: 'punct' | 'key' | 'string' | 'number' | 'comment';
  text: string;
}[][] = [[{
  tone: 'comment',
  text: '// Create a new document request'
}], [{
  tone: 'punct',
  text: 'POST '
}, {
  tone: 'string',
  text: '/v1/documents'
}], [{
  tone: 'punct',
  text: '{'
}], [{
  tone: 'punct',
  text: '  '
}, {
  tone: 'key',
  text: '"name"'
}, {
  tone: 'punct',
  text: ': '
}, {
  tone: 'string',
  text: '"Q3 Submission Subjectivities"'
}, {
  tone: 'punct',
  text: ','
}], [{
  tone: 'punct',
  text: '  '
}, {
  tone: 'key',
  text: '"workflow"'
}, {
  tone: 'punct',
  text: ': '
}, {
  tone: 'string',
  text: '"subjectivities_v2"'
}, {
  tone: 'punct',
  text: ','
}], [{
  tone: 'punct',
  text: '  '
}, {
  tone: 'key',
  text: '"due_date"'
}, {
  tone: 'punct',
  text: ': '
}, {
  tone: 'string',
  text: '"2026-05-12"'
}, {
  tone: 'punct',
  text: ','
}], [{
  tone: 'punct',
  text: '  '
}, {
  tone: 'key',
  text: '"reminders"'
}, {
  tone: 'punct',
  text: ': '
}, {
  tone: 'number',
  text: '3'
}, {
  tone: 'punct',
  text: ','
}], [{
  tone: 'punct',
  text: '  '
}, {
  tone: 'key',
  text: '"auto_validate"'
}, {
  tone: 'punct',
  text: ': '
}, {
  tone: 'number',
  text: 'true'
}, {
  tone: 'punct',
  text: ','
}], [{
  tone: 'punct',
  text: '  '
}, {
  tone: 'key',
  text: '"recipients"'
}, {
  tone: 'punct',
  text: ': ['
}], [{
  tone: 'punct',
  text: '    { '
}, {
  tone: 'key',
  text: '"email"'
}, {
  tone: 'punct',
  text: ': '
}, {
  tone: 'string',
  text: '"jane@broker.co"'
}, {
  tone: 'punct',
  text: ' }'
}], [{
  tone: 'punct',
  text: '  ],'
}], [{
  tone: 'punct',
  text: '  '
}, {
  tone: 'key',
  text: '"items"'
}, {
  tone: 'punct',
  text: ': ['
}, {
  tone: 'string',
  text: '"ACORD 25"'
}, {
  tone: 'punct',
  text: ', '
}, {
  tone: 'string',
  text: '"Loss runs"'
}, {
  tone: 'punct',
  text: ']'
}, {
  tone: 'punct',
  text: ','
}], [{
  tone: 'punct',
  text: '  '
}, {
  tone: 'key',
  text: '"webhook_url"'
}, {
  tone: 'punct',
  text: ': '
}, {
  tone: 'string',
  text: '"https://broker.co/hooks/pigeon"'
}], [{
  tone: 'punct',
  text: '}'
}]];
const toneClass: Record<string, string> = {
  punct: 'text-zinc-500',
  key: 'text-accent',
  string: 'text-emerald-300',
  number: 'text-amber-300',
  comment: 'text-zinc-600'
};
const events = [{
  label: 'Request created',
  meta: 'doc_8a2f',
  delay: 0
}, {
  label: 'Email sent to jane@broker.co',
  meta: '10:42 ET',
  delay: 0.15
}, {
  label: 'Reply received · 2 attachments',
  meta: '11:08 ET',
  delay: 0.3
}, {
  label: 'AI validated · all checks passed',
  meta: '11:09 ET',
  delay: 0.45
}];
const integrations = [{
  name: 'Outlook',
  logo: 'https://i.imgur.com/N0HAjSX.png'
}, {
  name: 'Gmail',
  logo: 'https://i.imgur.com/sbOTl27.png'
}, {
  name: 'ImageRight',
  logo: 'https://i.imgur.com/dwL552b.png'
}, {
  name: 'Vertafore',
  logo: 'https://i.imgur.com/s8wSIqV.png'
}, {
  name: 'Origami Risk',
  logo: 'https://i.imgur.com/4pNSiT4.png'
}, {
  name: 'Dyad',
  logo: 'https://i.imgur.com/mI7ssID.png'
}, {
  name: 'Guidewire',
  logo: 'https://i.imgur.com/pZnMhBh.png'
}, {
  name: 'Duck Creek',
  logo: 'https://i.imgur.com/UaSme4L.png'
}, {
  name: 'Snapsheet',
  logo: 'https://i.imgur.com/z9OsFZw.png'
}, {
  name: 'Sharepoint',
  logo: 'https://i.imgur.com/R2HP2ef.png'
}, {
  name: 'BindHQ',
  logo: 'https://i.imgur.com/iTgz2Bi.png'
}, {
  name: 'Applied Epic',
  logo: 'https://i.imgur.com/CLv63Ar.png'
}, {
  name: 'Salesforce',
  logo: 'https://i.imgur.com/ARGt4GI.png'
}, {
  name: 'HubSpot',
  logo: 'https://i.imgur.com/L4CTPky.png'
}, {
  name: 'Zoho CRM',
  logo: 'https://i.imgur.com/DkyqoiT.png'
}, {
  name: 'Pipedrive',
  logo: 'https://i.imgur.com/Bw4nlKD.png'
}, {
  name: 'And more',
  logo: 'https://i.imgur.com/AuKpjD7.png'
}, {
  name: 'OneDrive',
  logo: 'https://i.imgur.com/O4HZwjr.png'
}, {
  name: 'Google Drive',
  logo: 'https://i.imgur.com/VO1eicv.png'
}, {
  name: 'Box',
  logo: 'https://i.imgur.com/Ovl7PUi.png'
}, {
  name: 'Dropbox',
  logo: 'https://i.imgur.com/QscZMxB.png'
}, {
  name: 'Zapier',
  logo: 'https://i.imgur.com/GceTk9p.png'
}];
export function DeveloperAPI() {
  return <section id="api" className="relative overflow-hidden border-y border-white/5 bg-zinc-950 py-14 sm:py-28" data-id="element-606">
      <div aria-hidden className="pointer-events-none absolute -left-32 top-1/3 h-[28rem] w-[28rem] rounded-full bg-accent/[0.05] blur-[120px]" data-id="element-607" />
      <div aria-hidden className="pointer-events-none absolute -right-32 bottom-1/4 h-[24rem] w-[24rem] rounded-full bg-indigo-500/[0.05] blur-[120px]" data-id="element-608" />

      <Container className="relative" data-id="element-609">
        {/* Integrations header — eyebrow removed, copy + line break updated */}
        <div id="integrations" className="mx-auto max-w-4xl text-center" data-id="element-610">
          <h2 className="text-balance font-serif-display text-[2.85rem] leading-[1.02] tracking-tight text-white sm:text-5xl sm:leading-[1.0] lg:text-6xl" data-id="element-611">
            Integrates directly into
            <br data-id="element-612" />
            <span className="italic text-accent" data-id="element-613">your stack.</span>
          </h2>
          <p className="mx-auto mt-5 max-w-xl px-2 text-[15px] leading-relaxed text-zinc-400 sm:mt-6 sm:px-0 sm:text-lg" data-id="element-614">
            Our team builds every integration for you. Zero lift from your tech
            team.
          </p>
        </div>

        {/* Logo grid — Your systems and & more are now single-block size */}
        <div className="mt-12" data-id="element-615">
          {/* MOBILE — two-row auto-scrolling marquee. Conveys breadth without forcing the user to scroll past 24 boxes. */}
          <div className="sm:hidden" data-id="element-616">
            <div className="relative overflow-hidden [mask-image:linear-gradient(to_right,transparent_0%,black_8%,black_92%,transparent_100%)] [-webkit-mask-image:linear-gradient(to_right,transparent_0%,black_8%,black_92%,transparent_100%)]" data-id="element-617">
              {/* Row 1 — scrolls left */}
              <div className="flex w-max gap-2.5 animate-marquee-left" data-id="element-618">
                {[...integrations.slice(0, 11), ...integrations.slice(0, 11)].map((it, i) => <div key={`r1-${i}`} className="flex h-16 w-32 flex-shrink-0 items-center justify-center rounded-xl border border-white/10 bg-white p-3 shadow-sm" data-id="element-619">
                    <img src={it.logo} alt={it.name} className="max-h-8 w-auto max-w-[80%] object-contain" data-id="element-620" />
                  </div>)}
              </div>
              {/* Row 2 — scrolls right (opposite direction for visual interest) */}
              <div className="mt-2.5 flex w-max gap-2.5 animate-marquee-right" data-id="element-621">
                {[...integrations.slice(11), ...integrations.slice(11)].map((it, i) => <div key={`r2-${i}`} className="flex h-16 w-32 flex-shrink-0 items-center justify-center rounded-xl border border-white/10 bg-white p-3 shadow-sm" data-id="element-622">
                      <img src={it.logo} alt={it.name} className="max-h-8 w-auto max-w-[80%] object-contain" data-id="element-623" />
                    </div>)}
              </div>
            </div>

            {/* Bookend tiles on mobile — sized to match the marquee logo tiles (h-16 w-32), centered as a small pair to preserve "your systems → & more" framing without dominating the viewport */}
            <div className="mt-3 flex justify-center gap-2.5" data-id="element-624">
              <div className="relative flex h-16 w-32 flex-shrink-0 items-center justify-center overflow-hidden rounded-xl border border-sky-300/50 bg-gradient-to-br from-blue-600 via-sky-400 to-cyan-300 p-3 text-center shadow-lg shadow-sky-500/20" data-id="element-625">
                <span className="text-xs font-bold leading-tight tracking-tight text-white drop-shadow" data-id="element-626">
                  Your systems
                </span>
              </div>
              <div className="relative flex h-16 w-32 flex-shrink-0 items-center justify-center overflow-hidden rounded-xl border border-rose-300/50 bg-gradient-to-br from-fuchsia-500 via-rose-400 to-orange-300 p-3 text-center shadow-lg shadow-rose-500/20" data-id="element-627">
                <span className="text-xs font-bold leading-tight tracking-tight text-white drop-shadow" data-id="element-628">
                  &amp; more
                </span>
              </div>
            </div>
          </div>

          {/* DESKTOP — original full grid */}
          <ul className="hidden grid-cols-2 gap-2.5 sm:grid sm:grid-cols-3 sm:gap-3 md:grid-cols-4 lg:grid-cols-6" data-id="element-629">
            <motion.li initial={{
            opacity: 0,
            scale: 0.96
          }} whileInView={{
            opacity: 1,
            scale: 1
          }} viewport={{
            once: true,
            margin: '400px'
          }} transition={{
            duration: 0.35
          }} className="relative flex aspect-[16/9] items-center justify-center overflow-hidden rounded-xl border border-sky-300/50 bg-gradient-to-br from-blue-600 via-sky-400 to-cyan-300 p-3 text-center shadow-lg shadow-sky-500/20" data-id="element-630">
              <span className="text-sm font-bold leading-tight tracking-tight text-white drop-shadow sm:text-base" data-id="element-631">
                Your systems
              </span>
            </motion.li>

            {integrations.map((it, i) => <motion.li key={it.name} initial={{
            opacity: 0,
            scale: 0.96
          }} whileInView={{
            opacity: 1,
            scale: 1
          }} viewport={{
            once: true,
            margin: '400px'
          }} transition={{
            duration: 0.35,
            delay: (i + 1) % 6 * 0.04
          }} className="group relative flex aspect-[16/9] items-center justify-center rounded-xl border border-white/10 bg-white p-4 shadow-sm transition-all hover:-translate-y-0.5 hover:border-accent/40 hover:shadow-lg hover:shadow-accent/10" title={it.name} data-id="element-632">
                <img src={it.logo} alt={it.name} className="max-h-10 w-auto max-w-[80%] object-contain transition-transform group-hover:scale-105" data-id="element-633" />
              </motion.li>)}

            <motion.li initial={{
            opacity: 0,
            scale: 0.96
          }} whileInView={{
            opacity: 1,
            scale: 1
          }} viewport={{
            once: true,
            margin: '400px'
          }} transition={{
            duration: 0.35
          }} className="relative flex aspect-[16/9] items-center justify-center overflow-hidden rounded-xl border border-rose-300/50 bg-gradient-to-br from-fuchsia-500 via-rose-400 to-orange-300 p-3 text-center shadow-lg shadow-rose-500/20" data-id="element-634">
              <span className="text-sm font-bold leading-tight tracking-tight text-white drop-shadow sm:text-base" data-id="element-635">
                &amp; more
              </span>
            </motion.li>
          </ul>
        </div>

        {/* Pigeon API header — Inter font, plain italic on API */}
        <div className="mx-auto mt-16 max-w-4xl text-center sm:mt-36" data-id="element-636">
          <h2 className="flex flex-wrap items-center justify-center gap-2 text-[2.85rem] font-bold leading-[1.0] tracking-tight text-white sm:gap-3 sm:text-5xl lg:text-6xl" data-id="element-637">
            <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/3Ti5Jr9Zm1LRNz51qTKsea/1.png" alt="" aria-hidden className="h-[0.9em] w-auto" data-id="element-638" />
            <span data-id="element-639">
              Pigeon
              <span className="italic gradient-shine ml-1 inline-block pl-0.5 pr-2 sm:pl-2" data-id="element-640">
                API
              </span>
            </span>
          </h2>
          <p className="mx-auto mt-6 max-w-xl text-base leading-relaxed text-zinc-400 sm:text-lg" data-id="element-641">
            Programmatic document requests and deep integrations into the
            systems your team already uses. Built for teams that want Pigeon's
            automation behind their own product.
          </p>
          <div className="mt-8 flex justify-center" data-id="element-642">
            <a href="https://docs.pigeondocuments.com" target="_blank" rel="noopener noreferrer" className="group inline-flex items-center gap-2 rounded-lg border border-white/15 bg-white/[0.03] px-5 py-2.5 text-sm font-medium text-white outline-none transition-colors hover:border-accent/40 hover:bg-white/[0.06] focus-visible:ring-2 focus-visible:ring-accent/50" data-id="element-643">
              <CodeIcon className="h-3.5 w-3.5 text-accent" data-id="element-644" />
              API Docs
              <span className="text-accent transition-transform group-hover:translate-x-0.5" data-id="element-645">
                →
              </span>
            </a>
          </div>
        </div>

        {/* API Mockup */}
        <div className="relative mx-auto mt-14 max-w-5xl" data-id="element-646">
          <div aria-hidden className="pointer-events-none absolute -inset-x-10 -inset-y-6 rounded-[2rem] bg-accent/5 blur-3xl" data-id="element-647" />

          {/* MOBILE — compact, iOS-native: summarized request + arrow + tight events. No 14-line code wall, no horizontal scroll. */}
          <div className="relative overflow-hidden rounded-2xl border border-white/10 bg-zinc-950/80 shadow-2xl shadow-black/40 backdrop-blur sm:hidden" data-id="element-648">
            <div className="flex items-center justify-between border-b border-white/5 px-4 py-3" data-id="element-649">
              <div className="flex items-center gap-2" data-id="element-650">
                <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-accent" data-id="element-651" />
                <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-zinc-400" data-id="element-652">
                  Pigeon API · v1
                </span>
              </div>
              <span className="rounded-full border border-emerald-500/30 bg-emerald-500/10 px-2 py-0.5 font-mono text-[9px] uppercase tracking-[0.16em] text-emerald-400" data-id="element-653">
                200 OK
              </span>
            </div>

            {/* Request — summarized */}
            <div className="px-4 pt-3 pb-3" data-id="element-654">
              <div className="flex items-center gap-1.5 font-mono text-[9px] uppercase tracking-[0.18em] text-zinc-500" data-id="element-655">
                <CodeIcon className="h-3 w-3 text-accent" data-id="element-656" />
                Request
              </div>
              <div className="mt-2 rounded-lg border border-white/5 bg-black/30 px-3 py-2.5 font-mono text-[11px] leading-[1.7] text-zinc-300" data-id="element-657">
                <div data-id="element-658">
                  <span className="text-accent" data-id="element-659">POST</span>{' '}
                  <span className="text-emerald-300" data-id="element-660">/v1/documents</span>
                </div>
                <div className="text-zinc-500" data-id="element-661">{'{'}</div>
                <div data-id="element-662">
                  {'  '}
                  <span className="text-accent" data-id="element-663">"workflow"</span>
                  <span className="text-zinc-500" data-id="element-664">: </span>
                  <span className="text-emerald-300" data-id="element-665">"subjectivities_v2"</span>
                  <span className="text-zinc-500" data-id="element-666">,</span>
                </div>
                <div data-id="element-667">
                  {'  '}
                  <span className="text-accent" data-id="element-668">"recipient"</span>
                  <span className="text-zinc-500" data-id="element-669">: </span>
                  <span className="text-emerald-300" data-id="element-670">"jane@broker.co"</span>
                  <span className="text-zinc-500" data-id="element-671">,</span>
                </div>
                <div data-id="element-672">
                  {'  '}
                  <span className="text-accent" data-id="element-673">"auto_validate"</span>
                  <span className="text-zinc-500" data-id="element-674">: </span>
                  <span className="text-amber-300" data-id="element-675">true</span>
                </div>
                <div className="text-zinc-600" data-id="element-676">{'  // …'}</div>
                <div className="text-zinc-500" data-id="element-677">{'}'}</div>
              </div>
            </div>

            {/* Flow connector — request flows down into events */}
            <div className="flex items-center gap-2 px-4 py-2 border-y border-white/5 bg-white/[0.02]" data-id="element-678">
              <CornerDownRightIcon className="h-3 w-3 text-accent" data-id="element-679" />
              <span className="font-mono text-[9px] uppercase tracking-[0.18em] text-accent" data-id="element-680">
                Pigeon sends back
              </span>
            </div>

            {/* Webhook events — tightened */}
            <ul className="space-y-1.5 px-4 py-3" data-id="element-681">
              {events.map(e => <motion.li key={e.label} initial={{
              opacity: 0,
              x: -6
            }} whileInView={{
              opacity: 1,
              x: 0
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.35,
              delay: e.delay
            }} className="flex items-center gap-2.5 rounded-md border border-white/5 bg-white/[0.02] px-3 py-2" data-id="element-682">
                  <span className="flex h-3.5 w-3.5 flex-shrink-0 items-center justify-center rounded-full bg-accent/15" data-id="element-683">
                    <CheckIcon className="h-2 w-2 text-accent" strokeWidth={3.5} data-id="element-684" />
                  </span>
                  <div className="min-w-0 flex-1" data-id="element-685">
                    <div className="truncate text-[12px] leading-tight text-zinc-200" data-id="element-686">
                      {e.label}
                    </div>
                  </div>
                  <span className="flex-shrink-0 font-mono text-[9px] text-zinc-500" data-id="element-687">
                    {e.meta}
                  </span>
                </motion.li>)}
            </ul>
          </div>

          {/* DESKTOP — full code reference */}
          <div className="relative hidden overflow-hidden rounded-2xl border border-white/10 bg-zinc-950/80 shadow-2xl shadow-black/40 backdrop-blur sm:block" data-id="element-688">
            {/* Header bar */}
            <div className="flex items-center justify-between border-b border-white/5 px-5 py-3.5" data-id="element-689">
              <div className="flex items-center gap-2" data-id="element-690">
                <span className="h-2 w-2 animate-pulse rounded-full bg-accent" data-id="element-691" />
                <span className="font-mono text-[11px] uppercase tracking-[0.18em] text-zinc-400" data-id="element-692">
                  Pigeon API · v1
                </span>
              </div>
              <span className="rounded-full border border-emerald-500/30 bg-emerald-500/10 px-2.5 py-0.5 font-mono text-[10px] uppercase tracking-[0.16em] text-emerald-400" data-id="element-693">
                200 OK
              </span>
            </div>

            {/* Body — split */}
            <div className="grid lg:grid-cols-[1.05fr_1fr]" data-id="element-694">
              {/* Request panel */}
              <div className="border-b border-white/5 p-5 sm:p-6 lg:border-b-0 lg:border-r" data-id="element-695">
                <div className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.18em] text-zinc-500" data-id="element-696">
                  <CodeIcon className="h-3 w-3 text-accent" data-id="element-697" />
                  Request
                </div>

                <pre className="mt-4 overflow-x-auto font-mono text-[12.5px] leading-[1.7] text-zinc-300 sm:text-[13px]" data-id="element-698">
                  {requestLines.map((segments, i) => <div key={i} className="whitespace-pre" data-id="element-699">
                      {segments.map((seg, j) => <span key={j} className={toneClass[seg.tone]} data-id="element-700">
                          {seg.text}
                        </span>)}
                    </div>)}
                </pre>
              </div>

              {/* Response / Events panel */}
              <div className="p-5 sm:p-6" data-id="element-701">
                <div className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.18em] text-zinc-500" data-id="element-702">
                  <CornerDownRightIcon className="h-3 w-3 text-accent" data-id="element-703" />
                  Webhook events
                </div>

                <ul className="mt-4 space-y-2" data-id="element-704">
                  {events.map(e => <motion.li key={e.label} initial={{
                  opacity: 0,
                  x: -6
                }} whileInView={{
                  opacity: 1,
                  x: 0
                }} viewport={{
                  once: true
                }} transition={{
                  duration: 0.35,
                  delay: e.delay
                }} className="flex items-start gap-3 rounded-lg border border-white/5 bg-white/[0.02] px-3.5 py-2.5" data-id="element-705">
                      <span className="mt-0.5 flex h-4 w-4 flex-shrink-0 items-center justify-center rounded-full bg-accent/15" data-id="element-706">
                        <CheckIcon className="h-2.5 w-2.5 text-accent" strokeWidth={3} data-id="element-707" />
                      </span>
                      <div className="min-w-0 flex-1" data-id="element-708">
                        <div className="text-sm leading-tight text-zinc-200" data-id="element-709">
                          {e.label}
                        </div>
                        <div className="mt-1 font-mono text-[10.5px] tracking-wide text-zinc-500" data-id="element-710">
                          {e.meta}
                        </div>
                      </div>
                    </motion.li>)}
                </ul>
              </div>
            </div>

            {/* Footer / endpoints strip */}
            <div className="border-t border-white/5 px-5 py-3 sm:px-6" data-id="element-711">
              <div className="flex flex-wrap items-center gap-x-6 gap-y-2 font-mono text-[11px] text-zinc-500" data-id="element-712">
                <span className="text-zinc-400" data-id="element-713">Endpoints</span>
                <span data-id="element-714">
                  <span className="text-accent" data-id="element-715">POST</span> /v1/documents
                </span>
                <span data-id="element-716">
                  <span className="text-accent" data-id="element-717">GET</span> /v1/documents/:id
                </span>
                <span data-id="element-718">
                  <span className="text-accent" data-id="element-719">POST</span> /v1/webhooks
                </span>
              </div>
            </div>
          </div>
        </div>
      </Container>
    </section>;
}